import Layout from "Layout";
import { Activity, DollarSign, TrendingUp, Users, Plus, X, Eye, Share2, CheckCircleIcon, Mail, QrCode, Link as LinkIcon, ChevronDown, ChevronUp } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { use } from "i18next";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  acceptTeamInvite,
  addToTeamByQR,
  ConvertMyTeamsToStats,
  ConvertTeamListToRecentTeamItems,
  createNewTeam,
  getMyTeam,
  getMyTeamRequests,
  getPendingInvites,
  MyTeamList,
  rejectTeamInvite,
} from "./funtion";
import { MyJoinRequest, PendingInvites, RecentTeamItem, Stat, TeamsList } from "./type";
import { Helmet } from "react-helmet-async";
import TeamShareModal from "./components/TeamShareModal";
import "./style/index.scss";

// mock join code generator
const mockJoinCode = (teamId: number | string) =>
  `G9G3${String(teamId).padStart(4, "0")}`.toUpperCase().slice(0, 8);

const isToday = (date: string | Date) => {
  const d = new Date(date);
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

type TeamsListTab = "all" | "my-team" | "invited";
const TEAMS_LIST_TABS: TeamsListTab[] = ["all", "my-team", "invited"];

const TeamsDashboard: React.FC = () => {
  const [userTeamsStat, setUserTeamsStat] = useState<Stat[]>([]);
  const [recentTeams, setRecentTeams] = useState<RecentTeamItem[]>([]);

  const [teamsList, setTeamsList] = useState<TeamsList>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvites>([]);
  const [myTeamRequests, setMyTeamRequests] = useState<PendingInvites>([]);
  const actionRequest = useMemo(() =>
    pendingInvites.filter((invite) => (invite.status === "Requested" && invite.is_personal === false) || (invite.status === "Pending" && invite.is_personal === true)),
    [pendingInvites]
  );
  const pendingRequests = useMemo(() =>
    pendingInvites.filter((invite) => (invite.status === "Requested" && invite.is_personal === true) || (invite.status === "Pending" && invite.is_personal === false)),
    [pendingInvites]
  );
  const [isRequiresApprovalOpen, setIsRequiresApprovalOpen] = useState(true);
  const [isWaitingApprovalOpen, setIsWaitingApprovalOpen] = useState(false);
  const [reload, setReload] = useState<number>(0);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTeamsListTab: TeamsListTab = TEAMS_LIST_TABS.includes(
    searchParams.get("tab") as TeamsListTab
  )
    ? (searchParams.get("tab") as TeamsListTab)
    : "all";

  const handleTeamsListTabChange = (tab: TeamsListTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };
  const [isAdminForTeamsList, setIsAdminForTeamsList] = useState<number[]>([]);
  const [canLeaveTeam, setCanLeaveTeam] = useState<number[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ teamName: "", description: "" });
  const [shareModal, setShareModal] = useState<{
    open: boolean;
    teamName: string;
    team_id: string;
    joinCode: string;
    showSuccess: boolean;
  }>({ open: false, teamName: "", team_id: "", joinCode: "", showSuccess: false });

  const closeShareModal = () =>
    setShareModal((prev) => ({ ...prev, open: false }));

  const handleShareTeam = (team: { id: number; name: string }) => {
    setShareModal({
      open: true,
      teamName: team.name,
      team_id: String(team.id),
      joinCode: mockJoinCode(team.id),
      showSuccess: false,
    });
  };

  useEffect(() => {
    const onInit = async () => {
      try {
        const myTeams = await getMyTeam();
        // Get user ID from localStorage
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const myId = user.id;

        // Filter teams where current user is an admin or deputy
        const adminTeams = myTeams.filter((team) =>
          team.members?.some((member) => member.user_id === myId && (member.role === "Admin" || member.role === "Deputy"))
        );
        const canLeaveTeams = myTeams.filter((team) =>
          team.members?.some((member) => member.user_id === myId && member.role !== "Admin")
        );
        setIsAdminForTeamsList(adminTeams.map((team) => team.team_id));
        setCanLeaveTeam(canLeaveTeams.map((team) => team.team_id));

        const teams = await MyTeamList(myTeams);
        setTeamsList(teams);
        const recentItems = ConvertTeamListToRecentTeamItems(teams);
        setRecentTeams(recentItems);
        const [pending, joinRequests] = await Promise.all([
          getPendingInvites(),
          getMyTeamRequests()
        ]);

        setPendingInvites(pending);
        setMyTeamRequests(joinRequests);
        setUserTeamsStat(ConvertMyTeamsToStats(myTeams));
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    onInit();
  }, [reload]);

  useEffect(() => {
    async function checkJoinViaQR() {
      const data = localStorage.getItem("join_via_qr");
      if (data) {
        var signature = "";
        var expires = "";
        var team_id = "";
        data.split("&").forEach((param) => {
          const [key, value] = param.split("=");
          const cleanKey = key.replace("?", "");
          if (cleanKey && value) {
            if (cleanKey === "signature") {
              signature = value;
            } else if (cleanKey === "expires") {
              expires = value;
            } else if (cleanKey === "team_id") {
              team_id = value;
            }
          }
        });
        if (signature && expires && team_id) {
          const dataRes = await addToTeamByQR(signature, expires, parseInt(team_id));
          if (!dataRes.success) {
            toast.error("Link mã QR không hợp lệ hoặc đã hết hạn.");
          } else {
            toast.success("Tham gia nhóm thành công! Đang chờ trưởng nhóm phê duyệt.");
          }
        }

        localStorage.removeItem("join_via_qr");
      }
    }
    checkJoinViaQR();
  }, []);


  const handleCreateClick = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setFormData({ teamName: "", description: "" });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    alert(
      `Creating new team: ${formData.teamName} with description: ${formData.description}`
    );
    handleCloseDialog();
  };

  const filteredTeamsList = teamsList.filter((team) => {
    if (activeTeamsListTab === "my-team") return isAdminForTeamsList.includes(team.id);
    return true;
  });

  const displayList = activeTeamsListTab === "invited"
    ? myTeamRequests.filter(req =>
      req.role !== "Admin" &&
      (req.status === "Approved" || req.status === "Accepted" || req.status === "Requested")
    )
    : filteredTeamsList;

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  async function handleCreateTeam() {
    if (!formData.teamName.trim()) {
      toast.error("Team name is required");
      return;
    } else {
      try {
        const data = await createNewTeam(formData.teamName.trim());
        const createdName = formData.teamName.trim();
        handleCloseDialog();
        setShareModal({
          open: true,
          teamName: createdName,
          team_id: data.team.id,
          joinCode: mockJoinCode(data.team.id),
          showSuccess: true,
        });
        setReload((prev) => prev + 1);
      } catch (error) {
        toast.error("Failed to create team");
      }
      finally {
        //setReload(reload + 1);
      }
    }
  }

  return (
    <Layout>
      <>
        <Helmet>
          <title>
            {t("Teams_dashboard.navigation.teams")} | LUMIR
          </title>
        </Helmet>
        <nav className="breadcrumb__container mb-3">
          <div
            className="breadcrumb__gradient breadcrumb__gradient--left"
            data-gradient="left"
            style={{ display: "none" }}
          ></div>
          <ul className="breadcrumb" data-breadcrumb-scroll-container="">
            <li className="breadcrumb-item">
              <a href="/teams">{t("Teams_dashboard.navigation.teams")}</a>
            </li>
          </ul>
          <div
            className="breadcrumb__gradient breadcrumb__gradient--right"
            data-gradient="right"
            style={{ display: "none" }}
          ></div>
        </nav>



        {/* <div className="grid lg:!grid-cols-4 md:!grid-cols-3 grid-cols-1 gap-2 w-full">
          {userTeamsStat.map((stat, index) => (
            <div
              key={index}
              className={`flex flex-col p-4 bg-white rounded-lg cursor-pointer shadow-lg justify-between transition-all duration-300 hover:!shadow-2xl`}
            >
              <div className="flex flex-row items-center justify-between">
                <p className={`text-sm text-gray-600`}>{t(`Teams_dashboard.${stat.title}`)}</p>
                <div
                  className={`bg-[${stat.bgColor}] w-12 h-12 rounded-[8px] flex items-center justify-center`}
                >
                  {stat.icon}
                </div>
              </div>
              <div>
                <p className="xl:text-2xl lg:text-[24px] md:text-[18px] font-bold line-clamp-2 !m-0">
                  {stat.value}
                </p>
              </div>
            </div>
          ))}
        </div> */}

        <div className="grid lg:grid-cols-2 grid-cols-1 gap-4 mt-4 w-full">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t("RecentTeams")}
            </h3>
            {recentTeams.slice(0, 3).map((team, index) => (
              <div
                key={index}
                className="flex items-center justify-between mb-2 p-2 bg-gray-50 rounded-md hover:cursor-pointer transition-all duration-300 hover:!shadow-2xl"
                onClick={() => {
                  const isAdmin = isAdminForTeamsList.includes(team.id);
                  const canLeave = canLeaveTeam.includes(team.id);
                  navigate(`/teams/${team.id}`, {
                    state: {
                      isAdmin,
                      canLeave
                    }
                  });
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`bg-[#dbeafe] w-12 h-12 !rounded-[8px] flex items-center justify-center`}
                  >
                    <Users color="#2563eb" />
                  </div>
                  <div className="flex flex-col">
                    <p className="text-sm text-gray-900 m-0">{team.name}</p>
                    <p className="text-xs text-gray-500 m-0">
                      {team.members} {t("members")}
                    </p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs !rounded-full">
                  {team.status}
                </span>
              </div>
            ))}
          </div>
          <div className="bg-white p-6 !rounded-[20px] shadow-lg border border-[#ECEDF0]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[19px] font-bold text-[#111827] m-0">
                {t("PendingActions")}
              </h3>
              <span className="text-[12.5px] text-[#6B7280] font-medium">
                {pendingInvites.length} {t("items")}
              </span>
            </div>

            {pendingInvites.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center py-8 px-4 h-full min-h-[200px]">
                <div className="w-20 h-20 bg-gray-50 !rounded-full flex items-center opacity-50 justify-center mb-4 border border-dashed border-gray-300">
                  <CheckCircleIcon size={32} />
                </div>
                <h4 className="text-gray-800 font-medium text-lg mb-1">
                  {t("Teams_dashboard.team_topNotifications_noData_title")}
                </h4>
                <p className="text-gray-500 text-sm max-w-sm">
                  {t("Teams_dashboard.team_topNotifications_noData_desc")}
                </p>
              </div>
            )}

            {/* GROUP 1: REQUIRES YOUR APPROVAL */}
            {actionRequest.length > 0 && (
              <div className="mb-6 last:mb-0">
                <div
                  className="flex items-center gap-2 mb-3 px-0.5 cursor-pointer select-none group/title"
                  onClick={() => setIsRequiresApprovalOpen(!isRequiresApprovalOpen)}
                >
                  <span className="text-[12.5px] font-bold uppercase tracking-wider text-[#B8823A] group-hover/title:opacity-80 transition">
                    {t("Teams_dashboard.pending_actions_requires_approval")}
                  </span>
                  <span className="text-[11px] font-bold min-w-[18px] h-[18px] px-1.5 !rounded-full flex items-center justify-center bg-[#FBF3E4] text-[#B8823A]">
                    {actionRequest.length}
                  </span>
                  <div className="ml-auto text-[#9CA3AF]">
                    {isRequiresApprovalOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isRequiresApprovalOpen && (
                  <div className="flex flex-col gap-2 transition-all duration-300 max-h-[200px] overflow-y-auto scrollbar-none">
                    {actionRequest.map((action, index) => (
                      <div
                        key={`invite-${index}`}
                        className="flex items-start gap-3 p-3.5 !rounded-xl bg-white border border-[#EFE3D2] border-l-[3px] border-l-[#B8823A] shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex-none w-[38px] h-[38px] !rounded-[11px] flex items-center justify-center bg-[#DBEAFE] text-[#3B82F6]">
                          {action.status === "Pending" ? (
                            <Mail size={17} />
                          ) : (
                            <QrCode size={17} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="text-sm font-semibold text-[#111827] leading-tight">
                              {
                                action.status === "Pending" ?
                                  `${t("Teams_dashboard.invited_to")} ` :
                                  `${action.first_name} ${action.last_name} ${t("Teams_dashboard.requested_to_join")} `
                              }
                              <b className="font-bold">{action.teamName}</b>
                            </span>
                            <span className="flex-none text-[10.5px] font-bold px-2 py-0.5 !rounded-full bg-[#DBEAFE] text-[#3B82F6] uppercase tracking-wide">
                              {action.status === "Pending" ? "EMAIL" : "QR"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-1">
                            <div className="text-[12.5px] text-[#6B7280] leading-normal truncate font-medium">
                              {action.target_email || "No email provided"}
                            </div>
                            <div className="text-[11px] text-[#9CA3AF] whitespace-nowrap">
                              {isToday(action.invitedAt)
                                ? new Date(action.invitedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : new Date(action.invitedAt).toLocaleDateString("en-GB")}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <button
                            onClick={() => {
                              rejectTeamInvite(action.teamId, action.status === "Pending" ? "email" : "qr", action.target_user_id).then((success) => {
                                if (success) {
                                  setPendingInvites((prev) =>
                                    prev.filter((invite) => invite.teamId !== action.teamId)
                                  );
                                  setReload((p) => p + 1);
                                }
                              });
                            }}
                            title={t("Reject")}
                            className="w-[31px] h-[31px] !rounded-full border border-[#ECEDF0] flex items-center justify-center bg-white hover:bg-[#FEE2E2] hover:border-[#EF4444] transition-all duration-200 group/btn"
                          >
                            <X size={15} className="text-[#6B7280] group-hover/btn:text-[#DC2626]" />
                          </button>
                          <button
                            onClick={() => {
                              acceptTeamInvite(action.teamId, action.status === "Pending" ? "email" : "qr", action.target_user_id).then((success) => {
                                if (success) {
                                  setPendingInvites((prev) =>
                                    prev.filter((invite) => invite.teamId !== action.teamId)
                                  );
                                  setReload((p) => p + 1);
                                }
                              });
                            }}
                            title={t("Accept")}
                            className="w-[31px] h-[31px] !rounded-full flex items-center justify-center bg-[#22C55E] hover:bg-[#16A34A] shadow-sm hover:shadow-[0_3px_10px_-3px_rgba(22,163,74,0.4)] transition-all duration-200"
                          >
                            <svg viewBox="0 0 24 24" fill="none" className="w-[15px] h-[15px]">
                              <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* GROUP 2: WAITING FOR APPROVAL */}
            {pendingRequests.length > 0 && (
              <div className="mb-6 last:mb-0">
                <div
                  className="flex items-center gap-2 mb-3 px-0.5 cursor-pointer select-none bg-white group/title"
                  onClick={() => setIsWaitingApprovalOpen(!isWaitingApprovalOpen)}
                >
                  <span className="text-[12.5px] font-bold uppercase tracking-wider text-[#6B7280] group-hover/title:text-gray-900 transition">
                    {t("Teams_dashboard.pending_actions_waiting_approval")}
                  </span>
                  <span className="text-[11px] font-bold min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center bg-gray-100 text-[#6B7280]">
                    {pendingRequests.length}
                  </span>
                  <div className="ml-auto text-[#9CA3AF]">
                    {isWaitingApprovalOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isWaitingApprovalOpen && (
                  <div className="flex flex-col gap-2 transition-all duration-300 max-h-[200px] overflow-y-auto scrollbar-none">
                    {pendingRequests.map((request, index) => (
                      <div
                        key={`request-${index}`}
                        className="flex items-start gap-3 p-3.5 rounded-xl border border-[#ECEDF0] hover:shadow-sm transition-all duration-300"
                      >
                        <div className="flex-none w-[38px] h-[38px] rounded-[11px] flex items-center justify-center bg-[#E0F2FE] text-[#0EA5E9] dark:bg-[#1E3A8A] dark:text-[#93C5FD]">
                          {request.status === "Pending" ? (
                            <Mail size={17} />
                          ) : (
                            <QrCode size={17} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 ">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5 ">
                            <span className="text-sm font-semibold text-[#111827] leading-tight">
                              {(() => {
                                const name = (request.first_name || request.last_name) 
                                  ? `${request.first_name || ''} ${request.last_name || ''}`.trim() 
                                  : request.target_email || '';
                                return user.id === request.target_user_id 
                                  ? t("Teams_dashboard.request_to_join") + " " + request.teamName 
                                  : request.status === "Pending" 
                                    ? t("Teams_dashboard.pending_aproval", { name }) 
                                    : name + ' ' + t("Teams_dashboard.request_to_join") + " " + request.teamName;
                              })()}
                            </span>
                            <span className="flex-none text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-[#E0F2FE] text-[#0EA5E9] uppercase tracking-wide">
                              {request.status === "Pending" ? "EMAIL" : "QR"}
                            </span>
                          </div>
                          <div className="text-[11.5px] text-[#9CA3AF] mt-1">
                            {isToday(request.invitedAt)
                              ? new Date(request.invitedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : new Date(request.invitedAt).toLocaleDateString("en-GB")}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-[#6B7280] text-[11px] font-bold whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C99A46] animate-pulse"></span>
                            {t("Teams_dashboard.waiting_approval_status")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="teams-list-card mt-6 bg-white rounded-lg shadow-lg">
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t("TeamsList")}
              </h2>
              <p className="text-sm text-gray-500">{t("ManageYourTeams")}</p>
            </div>
            <button
              className="bg-[#e9b460] text-white px-4 py-2 rounded-md hover:opacity-[0.85] transition duration-300 flex items-center gap-2"
              onClick={handleCreateClick}
            >
              <Plus size={16} />
              {t("CreateNewTeam")}
            </button>
          </div>

          <div className="flex gap-4 px-4">
            {TEAMS_LIST_TABS.map((tab) => (
              <button
                key={tab}
                className={`pb-2 pt-3 text-sm font-medium border-b-2 -mb-px transition ${activeTeamsListTab === tab
                  ? "border-[#e9b460] text-[#e9b460]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => handleTeamsListTabChange(tab)}
              >
                {tab === "all" && t("AllTeams")}
                {tab === "my-team" && t("MyCreatedTeams")}
                {tab === "invited" && t("InvitedTeams")}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto h-[calc(100vh-400px)] max-h-[calc(100vh-400px)] scrollbar-none overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("TeamName")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("Members")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {
                      activeTeamsListTab === "invited" ? t("Mentor") : t("TotalTradeAccounts")
                    }
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("CreatedDate")}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${activeTeamsListTab === "invited" ? "text-left" : "text-right"}`}>
                    {activeTeamsListTab === "invited" ? t("Status") : t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                      {t("NoTeamsYet")}
                    </td>
                  </tr>
                ) : (
                  (displayList as any[]).map((team, index) => {
                    const id = activeTeamsListTab === "invited" ? team.teamId : team.id;
                    const name = activeTeamsListTab === "invited" ? team.teamName : team.name;
                    const createdDate = activeTeamsListTab === "invited"
                      ? (isToday(team.invitedAt)
                        ? new Date(team.invitedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : new Date(team.invitedAt).toLocaleDateString("en-GB"))
                      : team.createdDate;
                    const status = team.status;
                    return (
                      <tr
                        key={index}
                        className={
                          activeTeamsListTab === "invited" && !["Active", "Approve", "Approved", "Accepted"].includes(status)
                            ? "cursor-not-allowed opacity-60"
                            : "hover:bg-gray-50 cursor-pointer"
                        }
                        onClick={() => {
                          if (
                            activeTeamsListTab === "invited" &&
                            !["Active", "Approve", "Approved", "Accepted"].includes(status)
                          ) {
                            return;
                          }

                          const isAdmin = isAdminForTeamsList.includes(id);
                          const canLeave = canLeaveTeam.includes(id);

                          navigate(`/teams/${id}`, {
                            state: {
                              isAdmin,
                              canLeave,
                            },
                          });
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <Users size={16} className="text-blue-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                                {name}
                                {activeTeamsListTab !== "invited" && team.isLeader && (
                                  <span className="text-yellow-500">👑</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {activeTeamsListTab === "invited" ? team.role : team.description || ""}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {activeTeamsListTab === "invited" ? (team.team_member > 1 ? t("Teams_dashboard.n_team_members_plural", { count: team.team_member }) : t("Teams_dashboard.n_team_members", { count: team.team_member })) : (
                              team.members > 1 ? t("Teams_dashboard.n_team_members_plural", { count: team.members }) : t("Teams_dashboard.n_team_members", { count: team.members })
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activeTeamsListTab === "invited" ? team.team_mentor : team.total_trade_account}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {createdDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {activeTeamsListTab === "invited" ? (
                            <div className="flex items-center">
                              {
                                status === "Pending" ? (
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-[10px] font-bold uppercase rounded-full">
                                    {t("Pending")}
                                  </span>
                                ) : status === "Requested" ? (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[10px] font-bold uppercase rounded-full">
                                    {t("Requested")}
                                  </span>
                                ) : (status === "Approved" || status === "Accepted") ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold uppercase rounded-full">
                                    {t("Approved")}
                                  </span>
                                ) : status === "Rejected" ? (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-[10px] font-bold uppercase rounded-full">
                                    {t("Rejected")}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-[10px] font-bold uppercase rounded-full">
                                    {t("Unknown")}
                                  </span>
                                )
                              }
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                title={t("ViewDetail")}
                                aria-label={t("ViewDetail")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isAdminForTeamsList.includes(team.id)) {
                                    navigate(`/teams/${team.id}`, { state: { isAdmin: true } });
                                  } else {
                                    navigate(
                                      `/teams/${team.id}`,
                                      { state: { canLeave: canLeaveTeam.includes(team.id) } }
                                    )
                                  }
                                }}
                                className="p-2 rounded-md text-gray-500 hover:text-[#e9b460] hover:bg-gray-100 transition"
                              >
                                <Eye size={18} />
                              </button>
                              {isAdminForTeamsList.includes(team.id) && (
                                <button
                                  type="button"
                                  title={t("ShareTeam")}
                                  aria-label={t("ShareTeam")}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShareTeam({ id: team.id, name: team.name });
                                  }}
                                  className="p-2 rounded-md text-gray-500 hover:text-[#e9b460] hover:bg-gray-100 transition"
                                >
                                  <Share2 size={18} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {isCreateDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 m-0">
                  {t("CreateNewTeam")}
                </h3>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={handleCloseDialog}
                  aria-label={t("Close")}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="px-6 py-5">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("TeamName")}
                  </label>
                  <input
                    type="text"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateTeam();
                    }}
                    autoFocus
                    className="w-full p-2 border border-gray-300 rounded-md text-sm font-normal"
                    placeholder="e.g., Alpha Trading Team"
                  />
                </div>
                <div className="flex gap-3 bg-yellow-50 border !border-[#d09619] rounded-md p-3">
                  <div className="bg-[#fef9c3] w-9 h-9 shrink-0 rounded-[8px] flex items-center justify-center">
                    <Users size={18} color="#d09619" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm !text-black font-medium">
                      {t("TeamLeaderInformation")}
                    </span>
                    <span className="text-xs text-[#1d4edf] font-normal">
                      {t("TeamLeaderDesc")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6 flex gap-3 w-full">
                <button
                  className="flex w-1/2 justify-center px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 !rounded-md"
                  onClick={handleCloseDialog}
                >
                  {t("Cancel")}
                </button>
                <button
                  className="flex w-1/2 justify-center px-4 py-2 text-white !rounded-md bg-[#E9B460] hover:opacity-90 transition"
                  onClick={handleCreateTeam}
                >
                  {t("CreateTeam")}
                </button>
              </div>
            </div>
          </div>
        )}

        <TeamShareModal
          open={shareModal.open}
          onClose={closeShareModal}
          team_id={shareModal.team_id}
          teamName={shareModal.teamName}
          joinCode={shareModal.joinCode}
          showSuccess={shareModal.showSuccess}
        />
      </>
    </Layout>
  );
};

export default TeamsDashboard;
